import CryptoJS from 'crypto-js';
import JSEncrypt from 'jsencrypt';
import { isFlutter } from '@/utils/tools';

type ResData = {
  seed: string,
  publicKey: string,
  pubKeyVersion: string;
  encAlgVersion: string
}

type InitObj = {
  algorithm: 'AES' | 'DES',
  algorithmKey: string,
  iv: string,
  resData: ResData
}

type DeviceType = 'PC'|'MOBILE'|'H5'

type JSONParams = {
  method:string,
  URI:string,
  data:any,
  query?:any,
  oh?:string,
  dataType?:string
}

const preData = {
  nonce: 'toobit',
  preSharedSecret: {
    PC: 't&@b',
    MOBILE: 'tb@&',
    H5: 't@b&'
  },
  AES: {
    PC: '1a6d6bc1-142f-44',
    MOBILE: '2a1d8bc0-142f-47',
    H5: '13a4d9bc-042f-4x'
  },
  DES: {
    PC: '1a6d6bc1',
    MOBILE: '2a1d8bc0',
    H5: '13a4d9bc'
  }
};

const reqData = {
  timestamp: '',
  publicKey: '',
  oh: '',
  headers: '',
  headersKey: ''
};

let initData:InitObj = {
  algorithm: 'AES',
  algorithmKey: '',
  iv: '',
  resData: {
    seed: '',
    publicKey: '',
    pubKeyVersion: '',
    encAlgVersion: ''
  }
};

const init = (data:ResData, type:DeviceType = 'PC') => {

  if (isFlutter()) {
    type = 'MOBILE';
  }
  const preSecret:string = preData.preSharedSecret[type] as string || '';

  if (data.encAlgVersion === '1') {
    initData = {
      algorithm: 'AES',
      algorithmKey: `${preSecret}${data.seed}`,
      iv: preData.AES[type],
      resData: data
    };
  } else {
    initData = {
      algorithm: 'DES',
      algorithmKey: `${preSecret}${data.seed}`,
      iv: preData.DES[type],
      resData: data
    };
  }
  decrypt();
};

const getInitData = () => {
  return initData;
};

const decrypt = () => {
  const { algorithm, algorithmKey, iv, resData } = initData;
  const ciphertext =  CryptoJS.enc.Base64.parse(resData.publicKey);
  const keys =  CryptoJS.enc.Utf8.parse(algorithmKey);
  const cIv = CryptoJS.enc.Utf8.parse(iv.padEnd(16, '0'));
  const decrypted = CryptoJS[algorithm].decrypt(
    { ciphertext: ciphertext },
    keys,
    {
      iv: cIv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    }
  );
  reqData.publicKey =  decrypted.toString(CryptoJS.enc.Utf8);
  if (import.meta.env.MODE !== 'production') {
    localStorage.setItem('encryptPublicKeyInfo', JSON.stringify({
      preKey: resData.publicKey,
      text: ciphertext,
      publicKey: reqData.publicKey
    }));
  }
  return reqData.publicKey;
};

const createSign = (dataHash:string, hashKey:string) => {
  const signObj = CryptoJS.HmacSHA256(dataHash, hashKey);
  return signObj.toString();
};

const RSAEncrypt = (sign:string) => {
  const publicKey = reqData.publicKey;
  const encrypt = new JSEncrypt();
  encrypt.setPublicKey(publicKey);
  return encrypt.encrypt(sign);
};

const encrypt = (params:JSONParams) => {
  const dataHash = createDataHash(params);
  const hashKey = createHashKey();
  const sign = createSign(dataHash, hashKey);
  const encryptSign = RSAEncrypt(sign);
  if (import.meta.env.MODE !== 'production') {
    localStorage.setItem('encryptInfo', JSON.stringify({
      dataHash,
      hashKey,
      midSign: sign,
      sign: encryptSign,
      publicKey: reqData.publicKey
    }));
  }
  const options =  {
    p: initData.resData.pubKeyVersion,
    n: preData.nonce,
    t: reqData.timestamp,
    o: reqData.oh,
    s: encryptSign,
    h: reqData.headersKey
  };
  return options;
};

const dataHash = (data:any, h:string) => {
  const type = { '1': 'MD5', '2': 'SHA-256', '3': 'SHA-512' }[h] || 'MD5';
  if (typeof data !== 'string') {
    data = JSON.stringify(data);
  }
  const hashObj = CryptoJS[type](data);
  return hashObj.toString();
};

const jsonBodyHash = (data:any = {}, h:string) => {
  return dataHash(data, h);
};

const paramsHash = (data:any = {}, query:any = {}, o:string) => {
  const dataKeys = Object.keys(data);
  const queryKeys = Object.keys(query);
  const keys = o === '1' ? [...dataKeys, ...queryKeys].sort() : [...dataKeys, ...queryKeys].sort().reverse();
  let str = '';
  keys.forEach(key => {
    let value;
    if (data[key] !== undefined) {
      value = data[key];
    } else if (query[key] !== undefined) {
      value = query[key];
    } else {
      value = '';
    }
    str += `${key}=${encodeURIComponent(value)}&`;
  });
  return str.slice(0, -1);
};

const createHashKey = () => {
  const key1 = `${reqData.timestamp}_${preData.nonce}`;
  const mid = CryptoJS.HmacSHA256(reqData.timestamp, key1);
  const hashKeyObj =  CryptoJS.HmacSHA256(preData.nonce, mid);
  return hashKeyObj.toString();
};

function cleanObject(obj:any) {
  for (const key in obj) {
    if (obj[key] === undefined) {
      delete obj[key];
    } else if (Array.isArray(obj[key]) && obj[key].length === 0) {
      delete obj[key];
    } else if (typeof obj[key] === 'object' && Object.keys(obj[key]).length === 0) {
      delete obj[key];
    }
  }
  return obj;
}

const createDataHash = (jsonParams:JSONParams) => {
  const { method, URI, data, query, oh = '2_1', dataType } = jsonParams;
  const [o, h] = oh.split('_');
  let headersStr = '';
  let dataStr = '';
  reqData.oh = oh;
  reqData.timestamp = new Date().getTime().toString();
  if (method === 'POST') {
    let httpHeadersKey = '';
    let paramHeadersKey = '';
    let paramHeadersStr = '';
    if (dataType === 'multipart/form-data') {
      const cleanData = cleanObject({ ...data });
      const sortKeys = Object.keys(cleanData).sort();
      if (sortKeys.length > 0) {
        paramHeadersKey = sortKeys.join(';');
        paramHeadersStr = sortKeys.map(key => `${key}=${encodeURIComponent(cleanData[key])}`).join('&');
      }
      if (paramHeadersKey) {
        const mid = CryptoJS['MD5'](paramHeadersStr) + 'toobit' + reqData.timestamp;
        preData.nonce = CryptoJS['MD5'](mid).toString();
      }
      if (o === '1') {
        httpHeadersKey = 'tb-nonce;tb-timestamp';
        headersStr = `tb-nonce:${preData.nonce}\ntb-timestamp:${reqData.timestamp}\n`;
      } else {
        httpHeadersKey = 'tb-timestamp;tb-nonce';
        headersStr = `tb-timestamp:${reqData.timestamp}\ntb-nonce:${preData.nonce}\n`;
      }
      reqData.headersKey = paramHeadersKey ? `${httpHeadersKey}&${paramHeadersKey}` : httpHeadersKey;
      const headerParams = `${method}\n${URI}\n${reqData.headersKey}\n${headersStr}\n${dataStr}`;
      return dataHash(headerParams, h);
    } else {   // application/json
      let headersKey = '';
      const cleanData = cleanObject({ ...data });
      dataStr = jsonBodyHash(cleanData, h);
      if (o === '1') {
        headersKey = 'tb-nonce;tb-timestamp';
        headersStr = `tb-nonce:${preData.nonce}\ntb-timestamp:${reqData.timestamp}\n`;
      } else {
        headersKey = 'tb-timestamp;tb-nonce';
        headersStr = `tb-timestamp:${reqData.timestamp}\ntb-nonce:${preData.nonce}\n`;
      }
      reqData.headersKey = headersKey;
      const headerParams = `${method}\n${URI}\n${headersKey}\n${headersStr}\n${dataStr}`;
      return dataHash(headerParams, h);
    }
  }
};

/*  -----------  axios 添加 crypto header  start -----------  */

const addCryptoHeaders = (config:any) => {
  let cryptoHeaders:any = null;
  if (config.crypto) {
    const { encrypt } = useCrypto();
    const urlObj = new URL(config.url);
    const urlPath =   urlObj.pathname;
    cryptoHeaders = encrypt({
      method: config.method.toUpperCase(),
      URI: urlPath,
      data: config.data,
      dataType: config.headers['Content-Type']
    });
    if (cryptoHeaders) {
      const { p, n, t, o, s, h } = cryptoHeaders;
      const ch = {
        'tb-pv': p,
        'tb-oh': o,
        'tb-timestamp': t,
        'tb-nonce': n,
        'tb-signature': s,
        'tb-sign-headers': h
      };
      return { ...config.headers, ...ch };
    }
  }
  return config.headers;

};

/*  -----------  axios 添加 crypto header  end -----------  */

export const useCrypto = () => {
  return {
    initData,
    init,
    decrypt,
    getInitData,
    encrypt,
    addCryptoHeaders
  };
};

